"use client";

import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";

import {
  getInternshipCategories,
  searchInternshipCatalogue,
  type IInternshipCategory,
  type IInternshipCourse,
} from "@/api/internshipData";
import { INTERNSHIP_CONTAINER_BASE } from "@/widgets/internship/container";

import ApplyForm from "./applyForm";
import CourseCard, { type CourseCardTheme } from "./CourseCard";
import Spinner from "./Spinner";

/**
 * Internship Courses 1 (7003/7003).
 *
 * The OpportunitiesTabs catalogue, CMS-driven only for the heading and its
 * accent rule — the cards, chips and paging come from the live upGrad
 * internship service (see `@/api/internshipData`), fetched client-side.
 *
 * Ported from the source with the breakpoint translation documented in
 * `@/widgets/internship/container`: source `sm:` (640) → `min-[640px]:`,
 * source `md:` (768) → `tab:`, source `lg:` (1024) → `min-[1024px]:`.
 *
 * Deviations from the source, each deliberate:
 *  - Unselected chips used `bg-muted`, which compiles to nothing, leaving them
 *    as bare text. They get a real grey (#EAECF0, the token map's gray.300).
 *  - The Projects tab was already commented out upstream; it is not ported.
 *  - Apply has no handler yet — the application modal is a separate piece of
 *    work, so the button renders disabled rather than silently doing nothing.
 */

/** Matches the source: page size 6. */
const PAGE_SIZE = 6;
const SEARCH_DEBOUNCE_MS = 500;
/** Sentinel id for the "All" chip — the API takes "" for no category filter. */
const ALL_CATEGORY_ID = "1";

const DEFAULT_TITLE_COLOR = "#031F44"; // token map secondary.900
const DEFAULT_ACCENT_COLOR = "#9F1C33";
/**
 * Chip fallbacks. The widget owns them so the admin preview and the live page
 * resolve identically — `preparePreviewData` passes CMS values through as-is.
 * Unselected text has no fallback: the source set none, so it inherits.
 */
const DEFAULT_CHIP_ACTIVE_BG = "#9F1C33"; // primary.900
const DEFAULT_CHIP_ACTIVE_TEXT = "#FFFFFF"; // bankGradient
const DEFAULT_CHIP_BG = "#EAECF0"; // gray.300 — stands in for the dead `bg-muted`
/** The page wraps sections in `<main class="bg-gray-1">` (#f5f5f5). */
const SECTION_BACKGROUND_COLOR = "#FFFFFF";

interface InternshipCourses1Props {
  data?: {
    data?: {
      title?: string;
      title_color?: string;
      show_accent_rule?: boolean | number | string;
      accent_color?: string;
      search_placeholder?: string;
      search_bg_color?: string;
      search_border_color?: string;
      search_text_color?: string;
      search_placeholder_color?: string;
      search_icon?: string;
      search_icon_color?: string;
      category_chip_bg_color?: string;
      category_chip_text_color?: string;
      category_chip_active_bg_color?: string;
      category_chip_active_text_color?: string;
      card_background_color?: string;
      card_border_color?: string;
      card_title_color?: string;
      card_badge_bg_color?: string;
      card_badge_text_color?: string;
      card_detail_bg_color?: string;
      card_detail_text_color?: string;
      apply_button_label?: string;
      apply_button_bg_color?: string;
      apply_button_hover_bg_color?: string;
      apply_button_text_color?: string;
      brochure_button_label?: string;
      brochure_button_bg_color?: string;
      brochure_button_hover_bg_color?: string;
      brochure_button_text_color?: string;
      brochure_button_border_color?: string;
      brochure_button_icon?: string;
      load_more_label?: string;
      load_more_bg_color?: string;
      load_more_text_color?: string;
      status?: boolean | number | string;
    };
  };
}

/** CMS booleans arrive as true / 1 / "1" depending on the transport. */
const isOn = (value: unknown): boolean =>
  value === true || value === 1 || value === "1";

/** Only emit a CSS property when the editor actually set a value. */
const styleIf = (
  property: keyof CSSProperties,
  value?: string
): CSSProperties => (value?.trim() ? { [property]: value.trim() } : {});

const DEFAULT_SEARCH_PLACEHOLDER = "Search for Courses";
/** Custom property the placeholder colour is piped through (see the input below). */
const PLACEHOLDER_COLOR_VAR = "--internship-search-placeholder";
/** The source's shadcn Input hard-codes `placeholder:text-[#BEBEBE]`. */
const DEFAULT_SEARCH_PLACEHOLDER_COLOR = "#BEBEBE";

/**
 * The source renders the shadcn `<Input>`, whose base classes survive `twMerge`
 * and so are part of how the field actually looks:
 *
 *   flex h-9 w-full rounded-md bg-transparent text-sm shadow-sm transition-colors
 *   placeholder:text-[#BEBEBE] focus-visible:outline-none focus-visible:ring-1
 *   focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50
 *
 * `h-9` is what sets the height (36px) — the call site's `py-2` is inert against
 * it. `border-gray-200` is NOT Tailwind's grey here: the source project remaps
 * that token to #7C8493, so it is written as a literal. `shadow-sm` is Tailwind
 * v3, which is `shadow-xs` in this project's v4.
 */
const SEARCH_INPUT_CLASSNAME = [
  "flex h-9 w-full rounded-md border border-[#7C8493] bg-transparent",
  "pl-9 pr-4 py-2 text-sm shadow-xs transition-colors",
  "placeholder:[color:var(--internship-search-placeholder)]",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950",
  "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");

export default function InternshipCourses1({ data }: InternshipCourses1Props) {
  const sectionData = data?.data || (data as any) || {};

  const [categories, setCategories] = useState<IInternshipCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY_ID);
  const [rawSearch, setRawSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [courses, setCourses] = useState<IInternshipCourse[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  /** Guards against a slow first page overwriting a newer filter's results. */
  const requestIdRef = useRef(0);

  /**
   * Apply Now. Atom Paynetz redirects back to this page with `?transactionId=`,
   * so the modal must also be able to open straight onto its payment-outcome
   * step without the student having clicked anything.
   */
  const router = useRouter();
  const pathname = usePathname();

  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [applyCourse, setApplyCourse] = useState<IInternshipCourse | undefined>(
    undefined
  );
  const [transactionId, setTransactionId] = useState("");

  /**
   * Read on mount from `window.location` rather than `useSearchParams`, which
   * opts the whole route out of static rendering unless every consumer sits
   * behind a Suspense boundary. The value is only ever used after the student
   * has been redirected back, so it is client-only either way.
   */
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("transactionId");
    if (id) {
      setTransactionId(id);
      setIsApplyOpen(true);
    }
  }, []);

  const handleApply = useCallback((course: IInternshipCourse) => {
    setApplyCourse(course);
    setIsApplyOpen(true);
  }, []);

  const handleCloseApply = useCallback(() => {
    setIsApplyOpen(false);
    setApplyCourse(undefined);

    // Drop the transactionId so a refresh does not reopen the outcome screen.
    if (transactionId) {
      setTransactionId("");
      const params = new URLSearchParams(window.location.search);
      params.delete("transactionId");
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : (pathname ?? ""));
    }
  }, [transactionId, pathname, router]);

  // Debounce typing into the query that actually hits the API.
  useEffect(() => {
    const id = setTimeout(() => setSearchQuery(rawSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [rawSearch]);

  // Categories: "All" first, "OTHERS" last (source ordering).
  useEffect(() => {
    const controller = new AbortController();

    getInternshipCategories("courses", controller.signal).then((rows) => {
      const named = rows.filter((c) => c.type !== "OTHERS");
      const others = rows.filter((c) => c.type === "OTHERS");
      setCategories([
        { id: ALL_CATEGORY_ID, type: "All" },
        ...named,
        ...others,
      ]);
    });

    return () => controller.abort();
  }, []);

  // First page, refetched whenever the filter or query changes.
  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++requestIdRef.current;
    setIsLoading(true);

    searchInternshipCatalogue(
      {
        type: "courses",
        categoryId:
          selectedCategory === ALL_CATEGORY_ID ? "" : selectedCategory,
        searchTerm: searchQuery,
        skip: 0,
        limit: PAGE_SIZE,
      },
      controller.signal
    ).then((result) => {
      if (requestId !== requestIdRef.current) return; // superseded
      setCourses(result.data);
      setTotal(result.total);
      setPageNo(0);
      setIsLoading(false);
    });

    return () => controller.abort();
  }, [searchQuery, selectedCategory]);

  const fetchMore = useCallback(async () => {
    setIsFetchingMore(true);
    // `skip` is a PAGE INDEX — verified against the service.
    const result = await searchInternshipCatalogue({
      type: "courses",
      categoryId: selectedCategory === ALL_CATEGORY_ID ? "" : selectedCategory,
      searchTerm: searchQuery,
      skip: pageNo + 1,
      limit: PAGE_SIZE,
    });
    setCourses((prev) => [...prev, ...result.data]);
    setTotal(result.total);
    setPageNo((prev) => prev + 1);
    setIsFetchingMore(false);
  }, [selectedCategory, searchQuery, pageNo]);

  const hasMore = useMemo(
    () => courses.length > 0 && courses.length < total,
    [courses.length, total]
  );

  if (!isOn(sectionData.status)) {
    return null;
  }

  // Also used by Load More, which shares the selected chip's colours in the
  // source (`bg-primary-900 text-bankGradient` on both).
  const chipActiveBg =
    sectionData.category_chip_active_bg_color?.trim() || DEFAULT_CHIP_ACTIVE_BG;
  const chipActiveText =
    sectionData.category_chip_active_text_color?.trim() ||
    DEFAULT_CHIP_ACTIVE_TEXT;
  const chipBg = sectionData.category_chip_bg_color?.trim() || DEFAULT_CHIP_BG;
  // Empty = inherit, matching the source, which set no colour on unselected chips.
  const chipText = sectionData.category_chip_text_color?.trim() || undefined;

  // Empty values are passed through as-is: CourseCard treats blank as "keep the
  // built-in design", so nothing shifts until an editor sets a colour.
  const cardTheme: CourseCardTheme = {
    cardBackgroundColor: sectionData.card_background_color,
    cardBorderColor: sectionData.card_border_color,
    cardTitleColor: sectionData.card_title_color,
    badgeBackgroundColor: sectionData.card_badge_bg_color,
    badgeTextColor: sectionData.card_badge_text_color,
    detailBackgroundColor: sectionData.card_detail_bg_color,
    detailTextColor: sectionData.card_detail_text_color,
    applyLabel: sectionData.apply_button_label,
    applyBackgroundColor: sectionData.apply_button_bg_color,
    applyHoverBackgroundColor: sectionData.apply_button_hover_bg_color,
    applyTextColor: sectionData.apply_button_text_color,
    // Styling only — CourseCard draws this button solely for courses whose API
    // row carries a brochure_url.
    brochureLabel: sectionData.brochure_button_label,
    brochureBackgroundColor: sectionData.brochure_button_bg_color,
    brochureHoverBackgroundColor: sectionData.brochure_button_hover_bg_color,
    brochureTextColor: sectionData.brochure_button_text_color,
    brochureBorderColor: sectionData.brochure_button_border_color,
    brochureIcon: sectionData.brochure_button_icon,
  };

  // Load More falls back to the selected chip's colours, which is how the source
  // styles it (`bg-primary-900 text-bankGradient` on both). Set these to break
  // that link.
  const loadMoreLabel = sectionData.load_more_label?.trim() || "Load More";
  const loadMoreBg = sectionData.load_more_bg_color?.trim() || chipActiveBg;
  const loadMoreText =
    sectionData.load_more_text_color?.trim() || chipActiveText;

  const searchPlaceholder =
    sectionData.search_placeholder?.trim() || DEFAULT_SEARCH_PLACEHOLDER;
  const searchIcon = sectionData.search_icon?.trim() || "";
  const searchPlaceholderColor =
    sectionData.search_placeholder_color?.trim() || "";

  const title = sectionData.title?.trim() || "";
  const showAccentRule =
    sectionData.show_accent_rule === undefined ||
    isOn(sectionData.show_accent_rule);

  return (
    <section
      className="w-full"
      style={{ backgroundColor: SECTION_BACKGROUND_COLOR }}
    >
      <div
        className={`${INTERNSHIP_CONTAINER_BASE} py-8 min-[1024px]:py-12`}
      >
        <div className="mb-6 flex flex-col tab:flex-row tab:justify-between">
          <div>
            {title ? (
              <h2
                className="text-2xl min-[640px]:text-3xl font-bold text-center tab:text-left"
                style={{
                  color: sectionData.title_color?.trim() || DEFAULT_TITLE_COLOR,
                }}
              >
                {title}
              </h2>
            ) : null}
            {title && showAccentRule ? (
              <div
                aria-hidden="true"
                className="h-1 w-32 mt-2 mx-auto tab:mx-0"
                style={{
                  backgroundColor:
                    sectionData.accent_color?.trim() || DEFAULT_ACCENT_COLOR,
                }}
              />
            ) : null}
          </div>

          <div className="relative w-full tab:w-64 min-[1024px]:w-80 my-5 tab:mt-0">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={rawSearch}
              onChange={(e) => setRawSearch(e.target.value)}
              aria-label={searchPlaceholder}
              style={
                {
                  ...styleIf("backgroundColor", sectionData.search_bg_color),
                  ...styleIf("borderColor", sectionData.search_border_color),
                  ...styleIf("color", sectionData.search_text_color),
                  // ::placeholder can't be set inline, so the colour rides in on a
                  // custom property that the class below reads.
                  [PLACEHOLDER_COLOR_VAR]:
                    searchPlaceholderColor || DEFAULT_SEARCH_PLACEHOLDER_COLOR,
                } as CSSProperties
              }
              className={SEARCH_INPUT_CLASSNAME}
            />
            {searchIcon ? (
              <img
                src={searchIcon}
                alt=""
                aria-hidden="true"
                className="absolute left-3 top-1/2 -translate-y-[50%] h-4 w-4 object-contain"
              />
            ) : (
              /* lucide Search, inlined */
              <svg
                className="absolute left-3 top-1/2 -translate-y-[50%] text-gray-400 h-4 w-4"
                style={styleIf("color", sectionData.search_icon_color)}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            )}
          </div>
        </div>

        <div className="overflow-x-auto pb-2">
          <div className="flex flex-nowrap gap-2 mb-4 min-w-max">
            {categories.map((category) => {
              const isSelected = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setSelectedCategory(category.id)}
                  aria-pressed={isSelected}
                  style={{
                    backgroundColor: isSelected ? chipActiveBg : chipBg,
                    color: isSelected ? chipActiveText : chipText,
                  }}
                  className="px-3 min-[640px]:px-4 py-1 min-[640px]:py-1.5 rounded-full min-[640px]:text-sm tab:text-base transition-colors whitespace-nowrap uppercase"
                >
                  {category.type}
                </button>
              );
            })}
          </div>
        </div>

        {isLoading ? (
          <div className="w-full flex flex-col items-center justify-center mb-20">
            <Spinner size="h-20 w-20" />
            <p>We are fetching your courses! Hang on there 🚀🚀</p>
          </div>
        ) : courses.length === 0 ? (
          <p className="w-full text-center py-16 text-gray-500">
            No courses match your search.
          </p>
        ) : (
          <div className="grid grid-cols-1 min-[640px]:grid-cols-2 min-[1024px]:grid-cols-3 gap-4 tab:gap-6 mt-5">
            {courses.map((course, index) => (
              <CourseCard
                key={`${course.course_id}-${index}`}
                course={course}
                theme={cardTheme}
                onApply={handleApply}
              />
            ))}
          </div>
        )}

        {hasMore ? (
          <div className="flex justify-center w-full mt-5">
            <button
              type="button"
              onClick={fetchMore}
              disabled={isFetchingMore}
              style={{ backgroundColor: loadMoreBg, color: loadMoreText }}
              className="px-3 min-[640px]:px-4 py-1 min-[640px]:py-1.5 rounded-full min-[640px]:text-sm tab:text-base transition-colors whitespace-nowrap disabled:opacity-70"
            >
              {isFetchingMore ? (
                <Spinner size="h-8 w-8" color="text-[#FFFFFF]" />
              ) : (
                loadMoreLabel
              )}
            </button>
          </div>
        ) : null}
      </div>

      {isApplyOpen ? (
        <ApplyForm
          open={isApplyOpen}
          onClose={handleCloseApply}
          preselectedCourse={applyCourse}
          categories={categories}
          transactionId={transactionId || undefined}
        />
      ) : null}
    </section>
  );
}
